import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { getAdjustedPrice, isRetail, storeSettings, meetsMinimumPurchase, getsWholesalePrices, getEffectiveCustomerType, getFinalUnitPrice, getBestCartDiscountPct, getReachedGiftTier, getNextGiftTier } = useAuth();
  const [cart, setCart] = useState(() => {
    // 🛒 Hidratación sincrónica desde localStorage. Necesario porque
    // StrictMode ejecuta los effects 2 veces en desarrollo y la vieja
    // versión hacía race con el save effect vaciando el carrito.
    try {
      const raw = localStorage.getItem('bebida-bacana-cart');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  });
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem('bebida-bacana-favorites');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  });
  const [isCartOpen, setIsCartOpenState] = useState(false);
  // 🏪 Multi-Sucursal (B2B): sucursal activa en el catálogo. Los items que
  // agregue el usuario se etiquetan automáticamente con este nombre. Se
  // persiste en localStorage para que sobreviva a los reloads.
  const [activeBranch, setActiveBranchState] = useState(() => {
    try {
      return localStorage.getItem('bebida-bacana-active-branch') || '';
    } catch { return ''; }
  });
  const setActiveBranch = useCallback((name) => {
    const clean = (name || '').trim().slice(0, 60);
    setActiveBranchState(clean);
    try {
      if (clean) localStorage.setItem('bebida-bacana-active-branch', clean);
      else localStorage.removeItem('bebida-bacana-active-branch');
    } catch { /* ignore */ }
  }, []);

  // 🏪 Devuelve el id de línea del carrito para un productId dado en la
  // sucursal activa. Ejemplo: `Q_CHOPP::sucursal a`. Sin sucursal → productId.
  const resolveLineId = useCallback((productId, branchOverride) => {
    const b = ((branchOverride ?? activeBranch) || '').trim();
    return b ? `${productId}::${b.toLowerCase()}` : productId;
  }, [activeBranch]);
  // Método de pago seleccionado por el usuario en el checkout. Se comparte
  // globalmente para que el catálogo (ProductCard/FeaturedCard) reaccione y
  // muestre precios base cuando el método deshabilita descuentos por volumen.
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  // Notificación cuando se refrescan precios obsoletos del carrito.
  // Estructura: { count, items: [{name, oldPrice, newPrice}] }
  const [priceUpdate, setPriceUpdate] = useState(null);
  const [shippingData, setShippingData] = useState({
    customerName: '',
    businessName: '',
    address: '',
    city: '',
    cuit: '',
    requiresInvoice: false,
    phone: '',
    paymentMethod: 'efectivo',
    shippingMethod: ''
  });

  // Handle browser back button for cart modal
  const setIsCartOpen = useCallback((open) => {
    if (open) {
      // Push a new history state when opening cart
      window.history.pushState({ cartOpen: true }, '');
      setIsCartOpenState(true);
      // 🔄 Refrescar precios al abrir: carrito viejo puede tener precios obsoletos
      refreshCartPrices();
    } else {
      setIsCartOpenState(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * refreshCartPrices
   * Trae los precios actuales desde /api/products y actualiza cada ítem del
   * carrito. Si un producto desapareció (delete/desactivado), lo saca.
   * Si un producto cambió de precio, lo actualiza y muestra una notificación.
   *
   * Esto resuelve el problema de "carritos cargados que quedan días con
   * precios viejos".
   */
  const refreshCartPrices = useCallback(async () => {
    try {
      const stored = localStorage.getItem('bebida-bacana-cart');
      const current = stored ? JSON.parse(stored) : [];
      if (!current || current.length === 0) return;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const r = await fetch(`${API}/products`);
      if (!r.ok) return;
      const data = await r.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      const byId = new Map(items.map(p => [String(p.id || p._id || p.name), p]));

      const changes = [];
      const refreshed = [];
      for (const item of current) {
        if (item.isComboItem) {
          // Los combos por ahora se mantienen como están (no se refrescan)
          refreshed.push(item);
          continue;
        }
        if (item.isGift) {
          // Los regalos se sincronizan automáticamente por el efecto de tiers;
          // no los tocamos acá para evitar borrados accidentales.
          refreshed.push(item);
          continue;
        }
        // 🏪 Los items con branchName tienen un id compuesto `productId::branch`
        // — SIEMPRE resolver por productId (o el pedazo antes de "::"), no
        // por item.id, para no confundirlos con "producto eliminado".
        const lookupKey = String(item.productId || (item.id ? String(item.id).split('::')[0] : '') || item._id || item.name);
        const fresh = byId.get(lookupKey);
        if (!fresh) {
          // Producto eliminado/desactivado
          changes.push({ name: item.name, oldPrice: item.priceNumeric, newPrice: null });
          continue;
        }
        const oldP = Number(item.priceNumeric || 0);
        const newP = Number(fresh.priceNumeric || 0);
        if (oldP !== newP || JSON.stringify(item.quantityPrices || []) !== JSON.stringify(fresh.quantityPrices || [])) {
          changes.push({ name: fresh.name, oldPrice: oldP, newPrice: newP });
        }
        refreshed.push({
          ...item,
          priceNumeric: newP,
          price: fresh.price,
          quantityPrices: fresh.quantityPrices || [],
          discountPercent: fresh.discountPercent,
          imageUrl: fresh.imageUrl,
          name: fresh.name,
          // 🔧 Backfill de minQuantity / packSize (con defaults) para sanear
          // carritos guardados antes del fix del "pedido mínimo".
          packSize: Math.max(1, Number(fresh.packSize) || 1),
          minQuantity: Math.max(1, Number(fresh.minQuantity) || 1),
          stock: fresh.stock,
        });
      }
      if (changes.length > 0) {
        setCart(refreshed);
        setPriceUpdate({ count: changes.length, items: changes, at: Date.now() });
        // Auto-clear el aviso a los 10s
        setTimeout(() => setPriceUpdate(null), 10000);
      } else if (refreshed.length > 0) {
        // Aunque los precios no cambien, actualizamos el carrito si
        // agregamos campos faltantes (minQuantity/packSize) en items legacy.
        setCart(prev => {
          const needsBackfill = refreshed.some((it, idx) => {
            const p = prev[idx] || {};
            return p.minQuantity !== it.minQuantity || p.packSize !== it.packSize;
          });
          return needsBackfill ? refreshed : prev;
        });
      }
    } catch (e) {
      // si falla la red, dejamos el carrito como está
    }
  }, []);

  // Listen for popstate (back button) events
  useEffect(() => {
    const handlePopState = (event) => {
      // If cart is open and user pressed back, close the cart
      if (isCartOpen) {
        setIsCartOpenState(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isCartOpen]);

  // Load shipping from localStorage on mount (cart/favorites hydrated
  // synchronously via lazy useState initialisers — see above).
  useEffect(() => {
    const savedShipping = localStorage.getItem('bebida-bacana-shipping');
    if (savedShipping) {
      setShippingData(JSON.parse(savedShipping));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bebida-bacana-cart', JSON.stringify(cart));
  }, [cart]);

  // Save favorites to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bebida-bacana-favorites', JSON.stringify(favorites));
  }, []);

  // Save shipping data to localStorage
  const saveShippingData = (data) => {
    setShippingData(data);
    localStorage.setItem('bebida-bacana-shipping', JSON.stringify(data));
  };

  // Favorites management
  const toggleFavorite = (productId) => {
    setFavorites(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const isFavorite = (productId) => {
    return favorites.includes(productId);
  };

  const getPriceForQuantity = (product, quantity, useRawPrice = false) => {
    const baseUnitPrice = Number(product.priceNumeric || product.price || 0);
    // useRawPrice: precio de lista puro, sin recargos ni descuentos, usado
    // para calcular el threshold del recargo B2C.
    if (useRawPrice) {
      const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : null;
      const ignoreTiers = _eff === 'consumidor_final';
      if (ignoreTiers || !product.quantityPrices || product.quantityPrices.length === 0) {
        return baseUnitPrice;
      }
      let applicableUnitPrice = baseUnitPrice;
      for (let i = product.quantityPrices.length - 1; i >= 0; i--) {
        if (quantity >= product.quantityPrices[i].minQty) {
          applicableUnitPrice = product.quantityPrices[i].priceNumeric;
          break;
        }
      }
      return applicableUnitPrice;
    }
    // Precio final winner-takes-all (permDisc, cartDisc, tierQty) + recargo B2C.
    // Se pasa el método de pago seleccionado para respetar allowVolumeDiscounts.
    const rawTotal = getRawCartTotal();
    if (getFinalUnitPrice) return getFinalUnitPrice(product, rawTotal, quantity, selectedPaymentMethod);
    // Fallback (nunca debería alcanzarse).
    return getAdjustedPrice ? getAdjustedPrice(baseUnitPrice, rawTotal) : baseUnitPrice;
  };

  // Get raw cart total without any surcharge (used to check threshold)
  // Los regalos (isGift=true) NUNCA cuentan al raw total: no sumarían para
  // alcanzar el próximo tier ni afectan la lógica de descuentos.
  // Total 'base' del carrito para la barra de progreso y el cálculo de
  // tiers: usa el PRECIO DE LISTA (item.priceNumeric) × qty, sin acumular
  // descuentos por cantidad, sin recargos de forma de pago, sin recargo
  // minorista B2C. Esto matchea 1:1 con la línea "Subtotal (precio de
  // lista)" del carrito y evita la confusión "la barra dice un monto pero
  // el carrito otro".
  const getRawCartTotal = () => {
    return cart.reduce((total, item) => {
      if (item.isGift) return total;
      const unitList = Number(item.priceNumeric || 0);
      return total + (unitList * (item.quantity || 0));
    }, 0);
  };

  // Get raw price without surcharge (for threshold calculation)
  const getPriceForQuantityRaw = (product, quantity) => {
    const baseUnitPrice = product.priceNumeric || 0;
    // 🚫 Consumidor final NO recibe descuentos por cantidad.
    const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : null;
    if (_eff === 'consumidor_final') return baseUnitPrice;

    if (product.quantityPrices && product.quantityPrices.length > 0) {
      const sortedPrices = [...product.quantityPrices].sort((a, b) => b.minQty - a.minQty);
      for (const tier of sortedPrices) {
        if (quantity >= tier.minQty) {
          return tier.priceNumeric || baseUnitPrice;
        }
      }
    }
    return baseUnitPrice;
  };

  const addToCart = (product, quantity = 1) => {
    const safeProduct = {
      ...product,
      priceNumeric: product.priceNumeric || product.price || 0,
      price: product.price || product.priceNumeric || 0,
      // 🆕 Persistir explícitamente minQuantity y packSize en la línea del
      // carrito para que las operaciones +/-/input respeten los mínimos
      // aún cuando el objeto product original no está en memoria (ej: al
      // rehidratar desde localStorage).
      minQuantity: Math.max(1, Number(product.minQuantity) || 1),
      packSize: Math.max(1, Number(product.packSize) || 1),
    };
    // 🏪 Etiqueta de sucursal actual (opcional). El id de la línea del carrito
    // pasa a ser un compound id `productId::branch` para que el MISMO producto
    // se pueda repetir en varias líneas si están asignadas a sucursales
    // distintas. Sin sucursal → id = productId (backwards compatible).
    const branchName = (activeBranch || '').trim();
    const productId = safeProduct.id;
    const lineId = branchName ? `${productId}::${branchName.toLowerCase()}` : productId;

    // Bloqueo por stock — se acumula ACROSS branches: si el user tiene 3u
    // en "Sucursal A" y 2u en "Sucursal B" y stock=4, el próximo add es 400.
    const stock = safeProduct.stock;
    if (stock !== undefined && stock !== null) {
      const inCartSameProduct = cart
        .filter(item => (item.productId || item.id) === productId)
        .reduce((s, it) => s + (it.quantity || 0), 0);
      const reqTotal = inCartSameProduct + quantity;
      if (stock <= 0) {
        return { ok: false, reason: 'out_of_stock', message: `${safeProduct.name} sin stock disponible.` };
      }
      if (reqTotal > stock) {
        return { ok: false, reason: 'exceeds_stock', message: `Solo quedan ${stock} unidades de ${safeProduct.name} (contando las ${inCartSameProduct} que ya tenés en el carrito).`, available: stock };
      }
    }
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === lineId);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === lineId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...safeProduct, id: lineId, productId, quantity, branchName: branchName || null }];
    });
    return { ok: true };
  };

  const addComboToCart = (combo, comboQty = 1) => {
    if (!combo?.products?.length) return;
    const discountFactor = combo.originalTotal > 0 ? combo.comboPrice / combo.originalTotal : 1;
    // Recargo por consumidor final: se aplica al precio de LISTA de cada producto
    // ANTES de calcular el descuento del combo. Si el user no es consumidor
    // final, el factor es 1 (sin recargo).
    const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : null;
    const surchargePct = (_eff === 'consumidor_final' && storeSettings)
      ? (storeSettings.retailSurchargePercent || 0)
      : 0;
    const surchargeFactor = 1 + surchargePct / 100;
    setCart(prevCart => {
      let newCart = [...prevCart];
      combo.products.forEach(cp => {
        let unitPrice;
        if (combo.pricingMode === 'manual') {
          unitPrice = Math.round((cp.comboUnitPrice ?? cp.originalPrice) * surchargeFactor / 10) * 10;
        } else {
          unitPrice = Math.round((cp.originalPrice || 0) * surchargeFactor * discountFactor / 10) * 10;
        }
        const cartId = `combo-${combo.id}-${cp.productId}`;
        const addQty = cp.quantity * comboQty;
        const existing = newCart.find(item => item.id === cartId);
        if (existing) {
          newCart = newCart.map(item => item.id === cartId ? { ...item, quantity: item.quantity + addQty } : item);
        } else {
          newCart.push({
            id: cartId,
            name: `${cp.name} (${combo.name})${cp.bonificado ? ' BONIF' : ''}`,
            priceNumeric: unitPrice,
            price: `$ ${unitPrice.toLocaleString('es-AR')}`,
            quantity: addQty,
            image: cp.image || '',
            imageUrl: cp.image || '',
            packSize: 1,
            isComboItem: true,
            comboId: combo.id,
            comboName: combo.name,
          });
        }
      });
      return newCart;
    });
  };

  const updateQuantity = (productId, quantity) => {
    const item = cart.find(i => i.id === productId);
    if (item?.isComboItem) return;
    if (item?.isGift) return { ok: false, reason: 'gift_locked', message: 'Los regalos no se pueden editar.' };
    if (quantity <= 0) {
      removeFromCart(productId);
      return { ok: true };
    }
    if (item && item.stock !== undefined && item.stock !== null && quantity > item.stock) {
      return { ok: false, reason: 'exceeds_stock', message: `Solo quedan ${item.stock} unidades de ${item.name}.`, available: item.stock };
    }
    setCart(prevCart =>
      prevCart.map(i =>
        i.id === productId ? { ...i, quantity } : i
      )
    );
    return { ok: true };
  };

  // Set manual discount % for a cart line (0-100). Used only by sellers.
  // No aplica si el cliente tiene descuento permanente.
  const setItemManualDiscount = (productId, pct) => {
    const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
    setCart(prevCart => prevCart.map(i => i.id === productId ? { ...i, manualDiscountPct: clamped } : i));
  };


  const removeFromCart = (productId) => {
    // If removing a combo item, remove ALL items from the same combo
    const item = cart.find(i => i.id === productId);
    if (item?.isComboItem && item.comboId) {
      setCart(prevCart => prevCart.filter(i => i.comboId !== item.comboId));
    } else {
      setCart(prevCart => prevCart.filter(item => item.id !== productId));
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('bebida-bacana-cart');
  };

  // 🏪 Reasignar la sucursal de una línea del carrito. Si ya existe otra
  // línea del MISMO producto asignada a la nueva sucursal, mergea cantidades
  // y borra la línea original (evita duplicados accidentales).
  const setItemBranch = (lineId, newBranchName) => {
    const clean = (newBranchName || '').trim();
    setCart(prevCart => {
      const item = prevCart.find(i => i.id === lineId);
      if (!item || item.isGift || item.isComboItem) return prevCart;
      const productId = item.productId || item.id.split('::')[0];
      const newLineId = clean ? `${productId}::${clean.toLowerCase()}` : productId;
      if (newLineId === lineId) return prevCart;
      const target = prevCart.find(i => i.id === newLineId);
      if (target) {
        return prevCart
          .filter(i => i.id !== lineId)
          .map(i => i.id === newLineId ? { ...i, quantity: (i.quantity || 0) + (item.quantity || 0) } : i);
      }
      return prevCart.map(i =>
        i.id === lineId
          ? { ...i, id: newLineId, branchName: clean || null }
          : i
      );
    });
  };

  // ─────────────────────────────────────────────────────────────
  // Sistema de Premios por Volumen: sincronizar la línea de regalo
  // automáticamente con el tier alcanzado.
  //  - Si el subtotal RAW (sin regalos) alcanza un tier con regalo entregable
  //    → agregar/actualizar línea `isGift=true` con priceNumeric=0.
  //  - Si el subtotal baja del umbral (o el tier ya no tiene stock) → remover.
  //  - Si el método de pago bloquea descuentos por volumen → remover
  //    (el regalo es parte del beneficio por volumen).
  //  - Al AGREGAR un regalo nuevo, disparar confeti.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    // Requiere helpers del AuthContext.
    if (!getReachedGiftTier) return;
    const rawTotal = getRawCartTotal();
    const pmBlocks = selectedPaymentMethod && selectedPaymentMethod.allowVolumeDiscounts === false;
    const reached = pmBlocks ? null : getReachedGiftTier(rawTotal);

    setCart(prev => {
      const existingGift = prev.find(i => i.isGift && !i.isPromoGift);
      // Caso 1: no debe haber regalo. Remover si existe.
      if (!reached || !reached.giftProduct) {
        if (!existingGift) return prev;
        return prev.filter(i => !(i.isGift && !i.isPromoGift));
      }
      const gp = reached.giftProduct;
      const giftQty = Number(reached.giftQty || 1);
      const giftId = `gift-${gp.id}`;
      // Caso 2: ya existe y matchea → nada que hacer.
      if (existingGift && existingGift.id === giftId && existingGift.quantity === giftQty) {
        return prev;
      }
      // Caso 3: agregar / reemplazar.
      const withoutGift = prev.filter(i => !(i.isGift && !i.isPromoGift));
      const newGift = {
        id: giftId,
        productId: gp.id,
        name: gp.name,
        price: '$ 0',
        priceNumeric: 0,
        quantity: giftQty,
        image: gp.imageUrl || '',
        imageUrl: gp.imageUrl || '',
        packSize: 1,
        isGift: true,
        giftTierThreshold: Number(reached.threshold || 0),
        giftOriginalPrice: Number(gp.priceNumeric || 0),
        stock: gp.stock,
      };
      // Confeti sólo cuando pasamos de NO tener regalo (o de tener otro) a tener este.
      if (!existingGift || existingGift.id !== giftId) {
        try {
          confetti({
            particleCount: 160,
            spread: 90,
            startVelocity: 50,
            origin: { x: 0.5, y: 0.4 },
            colors: ['#F59E0B', '#EC4899', '#8B5CF6', '#10B981', '#06B6D4'],
            scalar: 1.1,
            disableForReducedMotion: true,
          });
          setTimeout(() => {
            try {
              confetti({ particleCount: 80, spread: 70, angle: 60, origin: { x: 0.1, y: 0.5 }, colors: ['#F59E0B', '#EC4899'], disableForReducedMotion: true });
              confetti({ particleCount: 80, spread: 70, angle: 120, origin: { x: 0.9, y: 0.5 }, colors: ['#8B5CF6', '#10B981'], disableForReducedMotion: true });
            } catch (e) { /* ignore */ }
          }, 200);
        } catch (e) { /* ignore */ }
      }
      return [...withoutGift, newGift];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.map(i => `${i.id}:${i.quantity}`).join('|'), storeSettings?.cartDiscountTiers, storeSettings?.cartDiscountAudience, selectedPaymentMethod?.key, selectedPaymentMethod?.allowVolumeDiscounts]);

  // ─────────────────────────────────────────────────────────────
  // 🎁 Promociones / Bonificaciones automáticas (2+1, caja Fernet →
  // Carpano, etc.). A diferencia del regalo por objetivo de carrito
  // (basado en el total $), estas se disparan por CANTIDAD de un producto.
  // Se consultan al backend (/api/promociones/apply) para que el cálculo sea
  // idéntico al del pedido confirmado, y se sincronizan como líneas
  // `isPromoGift=true` (que también son `isGift`, así no suman al total ni
  // se pueden editar). El servidor vuelve a validarlas al crear el pedido.
  // ─────────────────────────────────────────────────────────────
  const promoSig = cart
    .filter(i => !i.isPromoGift)
    .map(i => `${i.productId || i.id}:${i.quantity}:${i.isGift ? 'g' : ''}`)
    .join('|');
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) return;
    let cancelled = false;
    const run = async () => {
      // Items que disparan promos: NO regalos (ni de tier ni de promo).
      const baseItems = cart
        .filter(i => !i.isGift)
        .map(i => ({ productId: i.productId || i.id, quantity: i.quantity }));
      const eff = getEffectiveCustomerType ? getEffectiveCustomerType() : null;
      let seg = 'todos';
      if (eff === 'consumidor_final') seg = 'particular';
      else if (eff === 'comercio') seg = 'comercio';
      let gifts = [];
      if (baseItems.length > 0) {
        try {
          const r = await fetch(`${apiUrl}/api/promociones/apply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: baseItems, segmento: seg }),
          });
          if (r.ok) { const d = await r.json(); gifts = d.gifts || []; }
        } catch { /* red caída: no tocar el carrito */ return; }
      }
      if (cancelled) return;
      setCart(prev => {
        const existingPromo = prev.filter(i => i.isPromoGift);
        const newPromo = gifts.map(g => ({
          id: `promo-${g.productId}`,
          productId: g.productId,
          name: g.productName,
          price: '$ 0',
          priceNumeric: 0,
          quantity: g.quantity,
          image: g.imageUrl || '',
          imageUrl: g.imageUrl || '',
          packSize: 1,
          isGift: true,
          isPromoGift: true,
          promoName: g.promoName || 'Bonificación',
          giftOriginalPrice: Number(g.giftOriginalPrice || 0),
        }));
        // No-op si no cambió nada (evita re-render innecesario).
        const matches = existingPromo.length === newPromo.length &&
          newPromo.every(ng => existingPromo.find(ep => ep.id === ng.id && ep.quantity === ng.quantity));
        if (matches) return prev;
        // Confeti al aparecer una bonificación nueva.
        if (newPromo.length > existingPromo.length) {
          try {
            confetti({
              particleCount: 140, spread: 85, startVelocity: 48,
              origin: { x: 0.5, y: 0.4 },
              colors: ['#F59E0B', '#EC4899', '#10B981', '#06B6D4'],
              disableForReducedMotion: true,
            });
          } catch (e) { /* ignore */ }
        }
        const withoutPromo = prev.filter(i => !i.isPromoGift);
        return [...withoutPromo, ...newPromo];
      });
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoSig]);

  // Valor total de ahorro por regalo (suma de precios base de los productos
  // entregados como regalo, útil para mostrar en el resumen del carrito y WA).
  const getGiftSavings = () => {
    return cart.reduce((total, item) => {
      if (!item.isGift) return total;
      return total + Number(item.giftOriginalPrice || 0) * Number(item.quantity || 0);
    }, 0);
  };

  const getGiftItems = () => cart.filter(i => i.isGift);

  const [volumeTiers, setVolumeTiers] = useState({ enabled: true, tiers: [] });
  const [profileBonus, setProfileBonus] = useState({ enabled: true, discountPercent: 2, minTotal: 0 });

  // Cargar tiers desde el backend (descuentos por volumen total)
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) return;
    fetch(`${apiUrl}/api/volume-tiers`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setVolumeTiers(d); })
      .catch(() => { /* ignore */ });
    fetch(`${apiUrl}/api/profile-bonus`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProfileBonus(d); })
      .catch(() => { /* ignore */ });
  }, []);

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      if (item.isGift) return total; // Los regalos siempre cuentan 0.
      if (item.isComboItem) return total + ((item.priceNumeric || 0) * (item.quantity || 0));
      let unitPrice = getPriceForQuantity(item, item.quantity, false) || 0;
      // Aplicar descuento manual del vendedor si existe (mantiene consistencia
      // con getItemPrice para que el footer/subtotal del carrito coincidan
      // con la línea individual y con el order build al submit).
      const mdp = Number(item.manualDiscountPct || 0);
      if (mdp > 0 && mdp <= 100) unitPrice = Math.ceil(unitPrice * (1 - mdp / 100));
      return total + (unitPrice * (item.quantity || 0));
    }, 0);
  };

  const getCartCount = () => {
    // Los regalos (isGift) no cuentan como unidades elegidas por el cliente.
    return cart.reduce((count, item) => count + (item.isGift ? 0 : item.quantity), 0);
  };

  const formatPrice = (price) => {
    return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getItemPrice = (item) => {
    let base;
    if (item.isComboItem) base = item.priceNumeric;
    else base = getPriceForQuantity(item, item.quantity);
    // Aplicar descuento manual del vendedor si existe (0-100%).
    const mdp = Number(item.manualDiscountPct || 0);
    if (mdp > 0 && mdp <= 100) {
      base = Math.ceil(base * (1 - mdp / 100));
    }
    return base;
  };

  // ============ Descuento por volumen total del carrito ============
  // Se aplica únicamente al subtotal de items que NO tienen descuento por
  // cantidad ni provienen de un combo. Si un producto ya tiene precio
  // especial (combo o tier por cantidad) ese precio queda fijo y NO recibe
  // el descuento extra.
  const getEligibleSubtotal = () => {
    return cart.reduce((acc, item) => {
      if (item.isComboItem) return acc; // combos: precio fijo
      const tierPrice = getPriceForQuantityRaw(item, item.quantity);
      const basePrice = item.priceNumeric || 0;
      // Si el precio aplicado ya es < precio base, hay descuento por cantidad activo → no acumula
      if (tierPrice && basePrice && tierPrice < basePrice - 0.01) return acc;
      return acc + (tierPrice * (item.quantity || 0));
    }, 0);
  };

  const getApplicableVolumeTier = () => {
    if (!volumeTiers || !volumeTiers.enabled) return null;
    // 🚫 Consumidor final NO recibe descuentos por volumen.
    const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : null;
    if (_eff === 'consumidor_final') return null;
    // 🚫 Si el método de pago seleccionado bloquea descuentos por volumen,
    // los escalones globales tampoco aplican.
    if (selectedPaymentMethod && selectedPaymentMethod.allowVolumeDiscounts === false) return null;
    const elig = getEligibleSubtotal();
    if (elig <= 0) return null;
    let best = null;
    for (const t of (volumeTiers.tiers || [])) {
      if (elig >= t.minTotal && (!best || t.discountPercent > best.discountPercent)) {
        best = t;
      }
    }
    return best;
  };

  const getVolumeDiscountAmount = () => {
    const t = getApplicableVolumeTier();
    if (!t) return 0;
    return Math.round(getEligibleSubtotal() * (t.discountPercent / 100) * 100) / 100;
  };

  const getCartTotalWithVolumeDiscount = () => {
    return Math.max(0, getCartTotal() - getVolumeDiscountAmount());
  };

  // ============ Bonus por completar perfil ============
  // Se aplica si:
  //  - el bonus está habilitado en admin
  //  - hay un usuario logueado
  //  - el perfil tiene CUIT, condición IVA y ciudad cargados
  //  - el subtotal supera minTotal
  //  - el usuario nunca redimió este bonus (flag profileBonusUsed en user)
  // El descuento solo aplica al subtotal de items "limpios" (sin combo ni tier
  // de cantidad activo), igual que el resto de descuentos.
  const isProfileComplete = (user) => {
    if (!user) return false;
    return Boolean(user.cuit && user.condicionIva && user.city && (user.businessName || user.name));
  };
  const getProfileBonusAmount = (user) => {
    if (!profileBonus?.enabled || !user) return 0;
    if (user.profileBonusUsed) return 0;
    // Clientes vinculados al sistema viejo (histórico) NO son "primer pedido"
    // — ya tenían cuenta antes, sólo se están registrando ahora en la app.
    if (user.linked_legacy_id) return 0;
    if (!isProfileComplete(user)) return 0;
    // Subtotal elegible = items sin combo y sin tier activo, menos el descuento
    // por volumen (que también se aplica solo a items limpios).
    const elig = Math.max(0, getEligibleSubtotal() - getVolumeDiscountAmount());
    if (elig < (profileBonus.minTotal || 0)) return 0;
    return Math.round(elig * (Number(profileBonus.discountPercent) || 0) / 100 * 100) / 100;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        addComboToCart,
        updateQuantity,
        setItemManualDiscount,
        removeFromCart,
        clearCart,
        getCartTotal,
        getCartCount,
        formatPrice,
        isCartOpen: isCartOpen,
        setIsCartOpen,
        getPriceForQuantity,
        getItemPrice,
        shippingData,
        saveShippingData,
        favorites,
        toggleFavorite,
        isFavorite,
        // Retail customer helpers
        isRetail,
        storeSettings,
        getRawCartTotal,
        // Selected payment method (compartido con catálogo para respetar
        // el flag allowVolumeDiscounts).
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        getsWholesalePrices: () => getsWholesalePrices ? getsWholesalePrices(getRawCartTotal()) : true,
        meetsMinimumPurchase: () => meetsMinimumPurchase ? meetsMinimumPurchase(getRawCartTotal()) : true,
        // Volume discount
        volumeTiers,
        getEligibleSubtotal,
        getApplicableVolumeTier,
        getVolumeDiscountAmount,
        getCartTotalWithVolumeDiscount,
        // Profile bonus
        profileBonus,
        getProfileBonusAmount,
        isProfileComplete,
        // 🎁 Sistema de Premios por Volumen
        getGiftItems,
        getGiftSavings,
        // 🏪 Multi-Sucursal (B2B)
        activeBranch,
        setActiveBranch,
        setItemBranch,
        resolveLineId,
        // Refresh de precios para carritos viejos
        priceUpdate,
        dismissPriceUpdate: () => setPriceUpdate(null),
        refreshCartPrices,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
